import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { ApplicationDetail } from "@/lib/api/review";
import { ReadOnlyField, FileCard } from "./Shared";
import type { MakeUpdateFn, FieldStatus } from "./Shared";
import type { ApplicationStageStatus } from "@/lib/api/vendor";
import { cn } from "@/lib/utils";

// ─── Legal Representative section ─────────────────────────────────────────────

export function LegalRepresentativeContent({
  isWide,
  stageStatus,
  onMakeUpdate,
  updatedFields,
  application,
}: {
  isWide: boolean;
  stageStatus?: ApplicationStageStatus;
  onMakeUpdate?: MakeUpdateFn;
  updatedFields?: Set<string>;
  application?: ApplicationDetail;
}) {
  const { t } = useI18n();
  const data = application?.legalRepresentative;
  const fr = application?.fieldRejections;
  const fs = (key: string): FieldStatus | undefined => {
    if (updatedFields?.has(key)) return "updated";
    return fr?.[key]
      ? stageStatus === "update_required"
        ? "update_required"
        : "rejected"
      : undefined;
  };

  if (!data) return null;

  return (
    <View className={cn("items-start w-full", isWide ? "gap-0" : "px-page-x gap-4")}>
      {/* Row 1: Full name (Chinese + English) */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step1.legalNameChinese"
          value={data.fullNameChinese}
          flex={isWide}
        />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step1.legalNameEnglish"
          value={data.fullNameEnglish}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 2: Nationality */}
      <View className={isWide ? "px-10 py-2" : ""}>
        <ReadOnlyField labelKey="preVetting.step1.nationality" value={data.nationality} />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 3: Role + Department */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField labelKey="preVetting.step1.roleInCompany" value={data.role} flex={isWide} />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step1.department"
          value={data.department}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 4: Phone + Email */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField labelKey="preVetting.step1.contactPhone" value={data.phone} flex={isWide} />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField labelKey="preVetting.step1.contactEmail" value={data.email} flex={isWide} />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 5: ID document */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <AppText weight="medium" className="text-base text-supporting-text-40 pt-2">
          {t("preVetting.step1.idType")}
        </AppText>
        <View className="py-4">
          <AppText weight="medium" className="text-base text-dark-text-50">
            {data.idType}
          </AppText>
        </View>
        {data.idDocumentUrl != null ? (
          <FileCard
            url={data.idDocumentUrl}
            fieldStatus={fs("legal_representative_id_document")}
            onMakeUpdate={
              onMakeUpdate ? () => onMakeUpdate("legal_representative_id_document") : undefined
            }
          />
        ) : (
          <View className="pb-4">
            <AppText weight="medium" className="text-base text-dark-text-10">
              {t("vendor.application.detail.notUploaded")}
            </AppText>
          </View>
        )}
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 6: WeChat QR Code */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <AppText weight="medium" className="text-base text-supporting-text-40 pt-2">
          {t("preVetting.step1.wechatQr")}
        </AppText>
        {data.wechatQrUrl != null ? (
          <FileCard url={data.wechatQrUrl} />
        ) : (
          <View className="py-4">
            <AppText weight="medium" className="text-base text-dark-text-10">
              {t("vendor.application.detail.notUploaded")}
            </AppText>
          </View>
        )}
      </View>
    </View>
  );
}
